import { ComponentProps, CSSProperties } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import { ISiteSectionEntry } from '../../../../contentful/types/ISiteSection';
import { Label, StyledButton, StyledLink } from './SiteSectionItem.styles';
import { useElementWidth } from '../../../../hooks/useElementWidth';
import { SiteSectionIcon } from './SiteSectionIcon';
import { getUrlForPageId } from '../../../../utils/getUrlForLink';
import { useCommonData } from '../../../../common-data/useCommonData';
import { RouterLink } from '../../link/RouterLink';

interface INavItemProps {
	siteSection: ISiteSectionEntry;
	onSiteSectionIdChange: (id: string) => void;
	isCurrent: boolean;
	collapsed: boolean;
}

export function SiteSectionItem({
	siteSection,
	isCurrent,
	collapsed,
	onSiteSectionIdChange,
}: INavItemProps) {
	const { pathToPageIdMap } = useCommonData();
	const state = collapsed ? 'collapsed' : 'expanded';
	const ariaLabel = siteSection.fields.title;

	const { inlineSvg, icon } = siteSection.fields;

	const renderContent = () => (
		<>
			<SiteSectionIcon
				id={siteSection.fields.id}
				icon={icon}
				inlineSvg={
					inlineSvg && (
						<div
							dangerouslySetInnerHTML={{
								// sanitize the svg content to prevent potential cross-site scripting (XSS) attacks
								// eslint-disable-next-line @typescript-eslint/naming-convention
								__html: DOMPurify.sanitize(inlineSvg, {
									USE_PROFILES: { svg: true },
								}),
							}}
							aria-hidden
							className="site-section-icon"
						/>
					)
				}
				aria-hidden
				className="site-section-icon"
			/>
			<Label>{siteSection.fields.title}</Label>
		</>
	);

	if (siteSection.fields.landingPage) {
		return (
			<NavLink
				href={getUrlForPageId(
					siteSection.fields.landingPage.fields.id,
					pathToPageIdMap,
				)}
				data-state={state}
				aria-current={isCurrent ? 'page' : undefined}
				aria-label={ariaLabel}
			>
				{renderContent()}
			</NavLink>
		);
	}
	return (
		<NavButton
			data-state={state}
			aria-current={isCurrent ? true : undefined}
			onClick={() => onSiteSectionIdChange(siteSection.fields.id)}
			aria-label={ariaLabel}
		>
			{renderContent()}
		</NavButton>
	);
}

function NavLink({ children, ...props }: ComponentProps<typeof RouterLink>) {
	const { width, ref } = useElementWidth<HTMLAnchorElement>();

	return (
		<StyledLink
			{...props}
			ref={ref}
			style={
				{ '--link-width': width ? `${width}px` : undefined } as CSSProperties
			}
		>
			{children}
		</StyledLink>
	);
}

function NavButton({ children, ...props }: ComponentProps<'button'>) {
	const { width, ref } = useElementWidth<HTMLButtonElement>();

	return (
		<StyledButton
			{...props}
			ref={ref}
			style={
				{ '--link-width': width ? `${width}px` : undefined } as CSSProperties
			}
		>
			{children}
		</StyledButton>
	);
}
